import { BottomNavigation } from '@numa-group/ui.bottom-navigation';
import { TopNavigation, TopNavigationContextType, type TopNavigationProps } from '@numa-group/ui.top-navigation';
import { default as NextLink } from 'next/link';
import { useRouter } from 'next/router';
import { NextIntlClientProvider } from 'next-intl';

import { useBrand } from '@src/contexts/brand-context';
import { useCmsContent } from '@src/contexts/cms-content-context';
import { useProfile } from '@src/features/profile/contexts/profile-context';
import { useLocale } from '@src/hooks/use-locale';
import { i18nConfig } from '@src/i18n/locales';
import { Link, usePathname as useIntlPathname } from '@src/i18n/routing';

import { NavigationNative } from './navigation.native';
import { NavigationWhiteLabel } from './navigation.white-label';

const languageSwitcherOptions = i18nConfig.locales.map((locale: string) => {
  return {
    value: locale,
    label: locale.toUpperCase(),
  };
});

export const Navigation = () => {
  const cmsContent = useCmsContent();
  const { isWhiteLabel, brand, isNativeBrand } = useBrand();
  const { logout, ...profileQuery } = useProfile();

  const locale = useLocale();
  const { pathname, query } = useRouter();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { locale: _, ...queryWithoutLocale } = query;
  const intlPathname = useIntlPathname();

  const user = profileQuery.data
    ? {
        firstName: profileQuery.data.firstName ?? ' ',
        lastName: profileQuery.data.lastName ?? ' ',
      }
    : undefined;

  const content: TopNavigationContextType['content'] = {
    currency: cmsContent.ibe.currency,
    language: cmsContent.ibe.language,
    logout: cmsContent.misc.logout,
    profile: cmsContent.misc.navigationProfile,
  };

  const nextIntlLinkFactory: TopNavigationProps['linkFactory'] = (props) => {
    /**
     * next-intl links are only used for internal navigation.
     * Externals links can be handled with next/link.
     */
    if (props.href instanceof URL) {
      return <NextLink {...props} />;
    }

    return (
      <NextIntlClientProvider locale={locale}>
        <Link {...props} locale={props.locale || undefined} />
      </NextIntlClientProvider>
    );
  };

  const isMyTripDetailsPage = pathname.endsWith('/trips/[id]');
  const isMyTripsPage = pathname.startsWith('/[locale]/trips');

  const languageSwitcher = (
    <TopNavigation.LanguageSwitcher
      options={languageSwitcherOptions}
      selectedValue={locale}
      renderLink={(option) => (
        <Link
          href={{
            pathname: intlPathname,
            query: queryWithoutLocale,
          }}
          locale={option.value}
          scroll={false}
        >
          {option.label}
        </Link>
      )}
    />
  );

  if (isNativeBrand) {
    return (
      <NavigationNative
        languageSwitcher={languageSwitcher}
        isMyTripsPage={isMyTripsPage}
        content={content}
        onLogout={() => logout()}
        user={user}
      />
    );
  }

  if (isWhiteLabel) {
    return (
      <NavigationWhiteLabel
        languageSwitcher={languageSwitcher}
        isMyTripDetailsPage={isMyTripDetailsPage}
        content={content}
      />
    );
  }

  const localePrefix = locale === i18nConfig.defaultLocale ? '' : `/${locale}`;
  const corporateLink = cmsContent.siteLink.forCompaniesLinksV2?.[0]?.links?.[0];
  const searchUrl = new URL(`${localePrefix}/search`, brand.websiteUrl);
  const locationsUrl = new URL(`${localePrefix}/locations`, brand.websiteUrl);
  const offersUrl = new URL(`${localePrefix}${user ? '/offers-logged-in' : '/offers'}`, 'https://pages.numastays.com')
    .href;

  const countries =
    cmsContent.allCountries
      ?.sort((a, b) => (a.cities.length > b.cities.length ? -1 : 1))
      .map(({ cities, ...country }) => ({
        ...country,
        cities: cities
          .filter(({ numaBrandedProperties }) => !!numaBrandedProperties?.length)
          .map((city) => ({
            cityName: city.cityName,
            href: new URL(`${localePrefix}/locations/${country.countrySlug}/${city.citySlug}`, brand.websiteUrl).href,
          })),
      }))
      .filter(({ cities }) => !!cities.length) ?? [];

  const cities = cmsContent.allCities
    .filter(({ numaBrandedProperties }) => !!numaBrandedProperties?.length)
    .map((city) => ({
      enCityName: city.enCityName,
      cityName: city.cityName,
      slug: city.citySlug,
    }));

  return (
    <>
      <TopNavigation position="sticky" content={content} linkFactory={nextIntlLinkFactory}>
        {isMyTripDetailsPage ? (
          <>
            <TopNavigation.Logo {...brand.logo} href="/trips" desktopOnly />
            <TopNavigation.MobileBackButton href="/trips" />
          </>
        ) : (
          <TopNavigation.Logo {...brand.logo} href="/trips" />
        )}

        <TopNavigation.Links>
          <TopNavigation.Locations
            trigger={<TopNavigation.Link href={locationsUrl}>{cmsContent.misc.navigationLocations}</TopNavigation.Link>}
            countries={countries}
          />
          {corporateLink && (
            <TopNavigation.Link href={corporateLink.redirectUrl} target="_blank">
              {corporateLink.label}
            </TopNavigation.Link>
          )}
          <TopNavigation.Link href={offersUrl}>{cmsContent.ibe.navigationOffers}</TopNavigation.Link>
          <TopNavigation.Link href="/trips" isActive={isMyTripsPage}>
            {cmsContent.navigationTrips}
          </TopNavigation.Link>
        </TopNavigation.Links>

        <TopNavigation.Settings>{languageSwitcher}</TopNavigation.Settings>

        {user ? (
          <TopNavigation.AccountBadge user={user} accountHref="/profile" onLogout={() => logout()} />
        ) : (
          <TopNavigation.SignUpLink href="/auth">{cmsContent.misc.signup}</TopNavigation.SignUpLink>
        )}
      </TopNavigation>

      {!isMyTripDetailsPage && (
        <BottomNavigation
          locale={locale}
          content={{
            search: cmsContent.misc.navigationSearch,
            locations: cmsContent.misc.navigationLocations,
            location: cmsContent.ibe.locationTitle,
            dateSelectionTitle: cmsContent.ibe.datepickerTitle,
            more: cmsContent.misc.navigationMore,
            loadMore: cmsContent.ibe.loadMoreDates,
            confirmCta: cmsContent.ibe.confirmStep,
            guests: cmsContent.numberOfGuests,
            nights: cmsContent.numberOfNights,
            // TODO: pluralize messages
            adults: `{count, plural, =1 {${cmsContent.ibe.adult}} other {${cmsContent.ibe.adultPlural}}}`,
            children: `{count, plural, =1 {${cmsContent.ibe.child}} other {${cmsContent.ibe.childPlural}}}`,
            infants: `{count, plural, =1 {${cmsContent.ibe.infant}} other {${cmsContent.ibe.infantPlural}}}`,
            adultsDescription: cmsContent.adultsDescription,
            childrenDescription: cmsContent.childrenDescription,
            infantsDescription: cmsContent.infantsDescription,
          }}
          linkFactory={nextIntlLinkFactory}
        >
          <BottomNavigation.Search href={searchUrl} cities={cities} />
          <BottomNavigation.Link href={locationsUrl} icon="map-pin">
            {cmsContent.misc.navigationLocations}
          </BottomNavigation.Link>
          <BottomNavigation.Link href="/trips" icon="trip" isActive={isMyTripsPage}>
            {cmsContent.navigationTrips}
          </BottomNavigation.Link>
          {user ? (
            <BottomNavigation.Link href="/profile" icon="user" isActive={pathname.includes('/profile')}>
              {cmsContent.misc.navigationProfile}
            </BottomNavigation.Link>
          ) : (
            <BottomNavigation.Link href="/auth" icon="user">
              {cmsContent.misc.signup}
            </BottomNavigation.Link>
          )}
          <BottomNavigation.SideNavigation logo={brand.darkLogo} countries={countries}>
            <BottomNavigation.SideNavigation.Link href={offersUrl}>
              {cmsContent.ibe.navigationOffers}
            </BottomNavigation.SideNavigation.Link>
            {corporateLink && (
              <BottomNavigation.SideNavigation.Link href={corporateLink.redirectUrl} target="_blank">
                {corporateLink.label}
              </BottomNavigation.SideNavigation.Link>
            )}
            <BottomNavigation.SideNavigation.Link href="/trips">
              {cmsContent.navigationTrips}
            </BottomNavigation.SideNavigation.Link>

            {user ? (
              <BottomNavigation.SideNavigation.Link onClick={() => logout()}>
                {`${cmsContent.misc.logout} ↗`}
              </BottomNavigation.SideNavigation.Link>
            ) : (
              <BottomNavigation.SideNavigation.Button href="/auth">
                {cmsContent.misc.loginSignup}
              </BottomNavigation.SideNavigation.Button>
            )}
          </BottomNavigation.SideNavigation>
        </BottomNavigation>
      )}
    </>
  );
};
